type HeaderTab = {
  value: string;
};

type PersistTabAction<TabType> =
  | {
      type: "hydrate";
      tab: TabType;
    }
  | {
      type: "persist";
      tab: TabType;
    }
  | {
      type: "none";
    };

type GetPersistTabActionParams<TabType> = {
  tabs: readonly HeaderTab[];
  activeTab: TabType;
  persistedTab: string | null;
  pendingTab?: TabType | null;
};

/**
 * Resolves whether a route-backed tab should hydrate local state or persist the current fallback tab.
 *
 * @param {GetPersistTabActionParams<TabType>} params - Current tab state and URL state.
 * @returns {PersistTabAction<TabType>} The tab persistence action to apply.
 */
export const getPersistTabAction = <TabType extends string>(
  params: GetPersistTabActionParams<TabType>,
): PersistTabAction<TabType> => {
  const { tabs, activeTab, pendingTab, persistedTab } = params;
  const fallbackTab = tabs.at(0)?.value as TabType | undefined;
  const isActiveKnownTab = tabs.some((tab) => tab.value === activeTab);
  const isPersistedKnownTab = persistedTab
    ? tabs.some((tab) => tab.value === persistedTab)
    : false;

  if (pendingTab && pendingTab === activeTab) {
    return {
      type: "none",
    };
  }

  if (persistedTab && isPersistedKnownTab && activeTab !== persistedTab) {
    return {
      type: "hydrate",
      tab: persistedTab as TabType,
    };
  }

  if (!isActiveKnownTab && fallbackTab) {
    return {
      type: "hydrate",
      tab: fallbackTab,
    };
  }

  if (persistedTab) {
    return {
      type: "none",
    };
  }

  return {
    type: "persist",
    tab: activeTab,
  };
};
